import React, { useState, useEffect } from 'react';
import { 
  Heart, 
  Users, 
  BookOpen, 
  ShoppingBag, 
  MessageCircle,
  Star,
  ArrowRight,
  Play,
  CheckCircle,
  Globe,
  Shield,
  Zap,
  TrendingUp,
  Award,
  Clock,
  User,
  Smartphone,
  CreditCard
} from 'lucide-react';
import ExpertVerification from './ExpertVerification';

interface LandingPageProps {
  onGetStarted: () => void;
  onSignIn: () => void;
}

const LandingPage: React.FC<LandingPageProps> = ({ onGetStarted, onSignIn }) => {
  const [currentFeature, setCurrentFeature] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [showExpertVerification, setShowExpertVerification] = useState(false);

  // Auto-rotate features every 4 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentFeature((prev) => (prev + 1) % 3);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // Track mouse position for parallax effects
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth) * 100,
        y: (e.clientY / window.innerHeight) * 100,
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const features = [
    {
      id: 0,
      title: "AI-Powered Health Assistant",
      description: "Get instant, accurate health information powered by advanced AI technology",
      icon: Zap,
      color: "from-blue-500 to-purple-600",
      stats: "98% Accuracy"
    },
    {
      id: 1,
      title: "Expert Community Network",
      description: "Connect with verified health professionals and get personalized advice",
      icon: Users,
      color: "from-green-500 to-emerald-600",
      stats: "500+ Experts"
    },
    {
      id: 2,
      title: "Comprehensive Health Marketplace",
      description: "Access quality medications and health products with prescription support",
      icon: ShoppingBag,
      color: "from-orange-500 to-red-600",
      stats: "10,000+ Products"
    }
  ];

  const experts = [
    {
      name: "Dr. Ama Osei",
      specialization: "General Medicine",
      experience: "15+ years",
      followers: "1.2K",
      bio: "Passionate about preventive medicine and community health education in Ghana.",
      verified: true
    },
    {
      name: "Dr. Kwaku Mensah",
      specialization: "Mental Health",
      experience: "8+ years",
      followers: "890",
      bio: "Clinical psychologist specializing in anxiety, depression, and trauma therapy.",
      verified: true
    },
    {
      name: "Dr. Akosua Frimpong",
      specialization: "Pediatrics",
      experience: "12+ years",
      followers: "2.1K",
      bio: "Dedicated to child health and development with focus on nutrition and growth.",
      verified: true
    }
  ];

  const testimonials = [
    {
      name: "Kwame Asante",
      location: "Accra",
      content: "PharmPlus helped me understand my medication better. The expert advice was invaluable!",
      avatar: "KA",
      echoes: 24,
      category: "medication-guidance"
    },
    {
      name: "Ama Serwaa",
      location: "Kumasi",
      content: "The community support during my health journey has been amazing. So grateful!",
      avatar: "AS",
      echoes: 18,
      category: "community-support"
    },
    {
      name: "Kofi Mensah",
      location: "Cape Coast",
      content: "Quick delivery and genuine products. PharmPlus is now my go-to health platform.",
      avatar: "KM",
      echoes: 31,
      category: "product-quality"
    }
  ];

  const stats = [
    { label: "Active Users", value: "50K+", icon: Users },
    { label: "Expert Consultations", value: "25K+", icon: MessageCircle },
    { label: "Health Articles", value: "1K+", icon: BookOpen },
    { label: "Products Delivered", value: "100K+", icon: ShoppingBag }
  ];

  const handleExpertVerificationSubmit = (data: any) => {
    console.log('Expert verification data:', data);
    // In real app, this would submit to backend
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 overflow-hidden pb-20 lg:pb-0">
      {/* Floating Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div 
          className="absolute w-64 h-64 bg-gradient-to-r from-green-400/20 to-blue-400/20 rounded-full blur-3xl"
          style={{
            transform: `translate(${mousePosition.x * 0.02}px, ${mousePosition.y * 0.02}px)`,
            top: '10%',
            left: '10%'
          }}
        />
        <div 
          className="absolute w-96 h-96 bg-gradient-to-r from-purple-400/20 to-pink-400/20 rounded-full blur-3xl"
          style={{
            transform: `translate(${mousePosition.x * -0.01}px, ${mousePosition.y * -0.01}px)`,
            top: '60%',
            right: '10%'
          }}
        />
        <div 
          className="absolute w-48 h-48 bg-gradient-to-r from-orange-400/20 to-red-400/20 rounded-full blur-3xl"
          style={{
            transform: `translate(${mousePosition.x * 0.015}px, ${mousePosition.y * 0.015}px)`,
            bottom: '20%',
            left: '20%'
          }}
        />
      </div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center px-4 py-20">
        <div className="container-responsive text-center">
          {/* Logo Animation */}
          <div className="mb-8 flex justify-center">
            <div 
              className="w-20 h-20 bg-gradient-to-br from-green-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-2xl hover:scale-110 hover:rotate-3 transition-all duration-500 cursor-pointer"
              style={{
                transform: `scale(${1 + Math.sin(Date.now() * 0.001) * 0.05})`,
              }}
            >
              <Heart className="text-white" size={40} />
            </div>
          </div>

          {/* Main Heading */}
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 gradient-text leading-tight">
            Your Health,
            <br />
            <span className="text-green-600">Our Priority</span>
          </h1>

          {/* Subtitle */}
          <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
            Connect with trusted health experts, access quality medications, and join a supportive community 
            dedicated to your wellness journey in Ghana.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
            <button
              onClick={onGetStarted}
              className="btn-primary btn-lg flex items-center space-x-2 group"
            >
              <span>Get Started Free</span>
              <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={onSignIn}
              className="btn-secondary btn-lg flex items-center space-x-2 group"
            >
              <span>Sign In</span>
              <User size={20} className="group-hover:scale-110 transition-transform" />
            </button>
          </div>

          {/* Trust Indicators */}
          <div className="flex flex-wrap justify-center items-center gap-8 text-gray-600">
            <div className="flex items-center space-x-2">
              <Shield className="text-green-600" size={20} />
              <span className="font-medium">Verified Experts</span>
            </div>
            <div className="flex items-center space-x-2">
              <Globe className="text-blue-600" size={20} />
              <span className="font-medium">Ghana-Wide Coverage</span>
            </div>
            <div className="flex items-center space-x-2">
              <Award className="text-purple-600" size={20} />
              <span className="font-medium">Quality Assured</span>
            </div>
          </div>
        </div>

        {/* Built with bolt.new Badge */}
        <div className="fixed bottom-6 right-6 z-50">
          <a
            href="https://bolt.new"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center space-x-2 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-full px-4 py-2 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 hover:rotate-1 group"
          >
            <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
              <Zap className="text-white" size={12} />
            </div>
            <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900">
              Built with bolt.new
            </span>
          </a>
        </div>
      </section>

      {/* Interactive Three Pillars Section */}
      <section className="py-20 px-4 relative">
        <div className="container-responsive">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
              Three Pillars of <span className="gradient-text">Wellness</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Experience healthcare reimagined through AI-powered assistance, expert guidance, and comprehensive marketplace solutions.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              const isActive = currentFeature === index;
              
              return (
                <div
                  key={feature.id}
                  onClick={() => setCurrentFeature(index)}
                  className={`card-interactive p-8 cursor-pointer transition-all duration-500 ${
                    isActive 
                      ? `bg-gradient-to-br ${feature.color} text-white scale-105 shadow-2xl` 
                      : 'bg-white hover:shadow-xl'
                  }`}
                  style={{ animationDelay: `${index * 200}ms` }}
                >
                  <div className="text-center">
                    <div className={`w-16 h-16 mx-auto mb-6 rounded-2xl flex items-center justify-center transition-all duration-300 ${
                      isActive ? 'bg-white/20' : 'bg-gradient-to-br ' + feature.color
                    }`}>
                      <Icon size={32} className={isActive ? 'text-white' : 'text-white'} />
                    </div>
                    
                    <h3 className={`text-xl font-bold mb-4 ${isActive ? 'text-white' : 'text-gray-900'}`}>
                      {feature.title}
                    </h3>
                    
                    <p className={`mb-6 leading-relaxed ${isActive ? 'text-white/90' : 'text-gray-600'}`}>
                      {feature.description}
                    </p>
                    
                    <div className={`inline-block px-4 py-2 rounded-full font-medium ${
                      isActive ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-700'
                    }`}>
                      {feature.stats}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Expert Showcase */}
      <section className="py-20 px-4 bg-white/50 backdrop-blur-sm">
        <div className="container-responsive">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
              Meet Our <span className="gradient-text">Health Experts</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Connect with verified healthcare professionals who understand the Ghanaian healthcare landscape.
            </p>
            
            {/* Expert CTA */}
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-2xl p-8 mb-12 text-white">
              <h3 className="text-2xl font-bold mb-4">Ready to be more than just a voice in the clinic?</h3>
              <p className="text-lg mb-6 opacity-90">
                Join PharmaPlus as an Expert and shape Ghana's health future—one live session, article, and community moment at a time.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => setShowExpertVerification(true)}
                  className="bg-white text-green-600 px-8 py-3 rounded-xl font-bold hover:bg-gray-100 transition-colors flex items-center space-x-2"
                >
                  <Award size={20} />
                  <span>Become an Expert</span>
                </button>
                <button 
                  onClick={() => setShowExpertVerification(true)}
                  className="border-2 border-white text-white px-8 py-3 rounded-xl font-bold hover:bg-white hover:text-green-600 transition-colors"
                >
                  See How Verification Works
                </button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {experts.map((expert, index) => (
              <div
                key={expert.name}
                className="card-interactive p-6 group"
                style={{ animationDelay: `${index * 150}ms` }}
              >
                <div className="text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-blue-500 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                    {expert.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  
                  <div className="flex items-center justify-center space-x-2 mb-2">
                    <h3 className="text-xl font-bold text-gray-900">{expert.name}</h3>
                    {expert.verified && (
                      <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                        <CheckCircle className="text-white" size={16} />
                      </div>
                    )}
                  </div>
                  
                  <p className="text-green-600 font-medium mb-2">{expert.specialization}</p>
                  <p className="text-gray-600 text-sm mb-4">{expert.bio}</p>
                  
                  <div className="flex justify-center space-x-4 text-sm text-gray-500 mb-4">
                    <span>{expert.experience}</span>
                    <span>•</span>
                    <span>{expert.followers} followers</span>
                  </div>
                  
                  <button 
                    onClick={onSignIn}
                    className="btn-primary"
                  >
                    Follow Expert
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive AI Demo */}
      <section className="py-20 px-4">
        <div className="container-responsive">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
                AI-Powered <span className="gradient-text">Health Assistant</span>
              </h2>
              <p className="text-xl text-gray-600">
                Experience instant, accurate health information powered by advanced AI technology.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-100">
              <div className="space-y-4">
                {/* User Question */}
                <div className="flex justify-end">
                  <div className="bg-green-600 text-white rounded-2xl rounded-br-md px-6 py-3 max-w-xs">
                    <p>Can I take paracetamol with ibuprofen?</p>
                  </div>
                </div>

                {/* AI Response */}
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                    <Zap className="text-white" size={20} />
                  </div>
                  <div className="bg-gray-100 rounded-2xl rounded-bl-md px-6 py-3 flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="font-medium text-gray-900">PharmPlus AI</span>
                      <div className="flex items-center space-x-1 text-xs text-green-600">
                        <CheckCircle size={12} />
                        <span>Verified Response</span>
                      </div>
                    </div>
                    <p className="text-gray-800 leading-relaxed">
                      Yes, paracetamol and ibuprofen can generally be taken together safely. They work differently - 
                      paracetamol reduces pain and fever, while ibuprofen also reduces inflammation. However, 
                      always consult with a healthcare provider for personalized advice.
                    </p>
                    <div className="flex items-center space-x-4 mt-3 text-xs text-gray-500">
                      <span>98% Accuracy</span>
                      <span>•</span>
                      <span>Response time: 0.8s</span>
                      <span>•</span>
                      <span>Verified by Dr. Ama Osei</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Community Testimonials */}
      <section className="py-20 px-4 bg-gradient-to-br from-green-50 to-blue-50">
        <div className="container-responsive">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
              Community <span className="gradient-text">Echoes</span>
            </h2>
            <p className="text-xl text-gray-600">
              Real stories from our health community across Ghana.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={testimonial.name}
                className="card-interactive p-6"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-pink-500 rounded-full flex items-center justify-center text-white font-bold">
                    {testimonial.avatar}
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">{testimonial.name}</h4>
                    <p className="text-gray-600 text-sm">{testimonial.location}</p>
                  </div>
                </div>
                
                <p className="text-gray-800 mb-4 leading-relaxed">"{testimonial.content}"</p>
                
                <div className="flex items-center justify-between">
                  <span className="badge-primary">
                    {testimonial.category.replace('-', ' ')}
                  </span>
                  <div className="flex items-center space-x-1 text-green-600">
                    <Heart size={16} />
                    <span className="text-sm font-medium">{testimonial.echoes} echoes</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive Payment Demo */}
      <section className="py-20 px-4">
        <div className="container-responsive">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
                <span className="gradient-text">Mobile Money</span> First
              </h2>
              <p className="text-xl text-gray-600">
                Seamless payments designed for Ghana with Mobile Money prioritized.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="card-interactive p-8 group">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                    <Smartphone className="text-white" size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Mobile Money</h3>
                  <p className="text-gray-600 mb-6">MTN • Vodafone • AirtelTigo</p>
                  <div className="badge-success">
                    Recommended
                  </div>
                </div>
              </div>

              <div className="card-interactive p-8 group">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                    <CreditCard className="text-white" size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Card Payments</h3>
                  <p className="text-gray-600 mb-6">Visa • Mastercard • Verve</p>
                  <div className="badge-info">
                    Secure & Fast
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Animated Statistics */}
      <section className="py-20 px-4 bg-white/50 backdrop-blur-sm">
        <div className="container-responsive">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
              Trusted by <span className="gradient-text">Thousands</span>
            </h2>
            <p className="text-xl text-gray-600">
              Join Ghana's fastest-growing health community.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div
                  key={stat.label}
                  className="text-center group animate-fade-in"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                    <Icon className="text-white" size={32} />
                  </div>
                  <div className="text-3xl md:text-4xl font-bold text-gray-900 mb-2 group-hover:scale-110 transition-transform duration-300">
                    {stat.value}
                  </div>
                  <div className="text-gray-600 font-medium">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-green-600 to-blue-600 text-white">
        <div className="container-responsive text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-6">
            Ready to Transform Your Health Journey?
          </h2>
          <p className="text-xl mb-8 opacity-90 max-w-3xl mx-auto">
            Join thousands of Ghanaians who trust PharmPlus for their health and wellness needs.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button
              onClick={onGetStarted}
              className="bg-white text-green-600 px-8 py-4 rounded-xl font-bold text-lg hover:bg-gray-100 transition-all duration-200 hover:scale-105 hover:shadow-xl flex items-center space-x-2"
            >
              <span>Start Your Journey</span>
              <ArrowRight size={20} />
            </button>
            <button
              onClick={onSignIn}
              className="border-2 border-white text-white px-8 py-3 rounded-xl font-bold text-lg hover:bg-white hover:text-green-600 transition-all duration-200 hover:scale-105"
            >
              Sign In
            </button>
          </div>
        </div>
      </section>

      {/* Expert Verification Modal */}
      {showExpertVerification && (
        <ExpertVerification
          onClose={() => setShowExpertVerification(false)}
          onSubmit={handleExpertVerificationSubmit}
        />
      )}
    </div>
  );
};

export default LandingPage;